import { AgentChat as AgentChatProviderNamespace } from "./agent-chat-provider";
import { AgentChatContextWindow } from "./parts/context-window";
import { AgentChatDeleteButton } from "./parts/delete-chat-button";
import { AgentChatHeader } from "./parts/header";
import { AgentChatHistoryButton } from "./parts/history-button";
import { AgentChatModelSelector } from "./parts/model-selector";
import { AgentChatNewChatButton } from "./parts/new-chat-button";
import { AgentChatPromptInput, AgentChatSubmit } from "./parts/prompt-input";
import { AgentChatRoot } from "./parts/root";
import { AgentChatSettings } from "./parts/settings";
import { AgentChatThread } from "./parts/thread";
import { AgentChatTurn } from "./parts/turn";

export type { AgentChatProviderProps } from "./agent-chat-provider";
export { useAgentChat, useCreateThread, useDeleteThread } from "./agent-chat-provider";
export type { ChatRouteContext } from "./chat-context";
export { buildChatContext } from "./chat-context";
export type { ChatAuth, ChatFetch } from "./chat-fetch";
export { makeChatFetch } from "./chat-fetch";
export { nextThreadAfterDelete, parseChatThreadCreatedAt, sortChatThreadsByCreatedAt } from "./chat-thread-order";
export { resolveChatThreadTitle, TITLE_PENDING } from "./chat-thread-title";
export type { ChatSource } from "./citations";
// The command face (MIN-298). `ThreadId` comes from ./thread and `Turn` from
// ./turn — the factory imports both rather than redeclaring them, so there is
// one name per concept on the barrel (the two-encodings shape MIN-297 removed).
export type { AgentChat as AgentChatClient, AgentTransport, CreateThreadInput } from "./create-agent-chat";
export { createAgentChat } from "./create-agent-chat";
// MIN-316 — the chat's one readable measure, shared by `Thread` and
// `PromptInput`. Exported so a host composing its own transcript surface lines
// up with the composer instead of re-deriving a width.
export { CHAT_MEASURE_MAX_WIDTH, CHAT_MEASURE_PADDING_INLINE, chatMeasureStyle } from "./elements/chat-measure";
export type { ChatMessageUsage } from "./message";
export type { CreateChatThreadInput } from "./mutations";
// MIN-293 — the compound's own context: `useAgentChatContext` is the seam
// every connected part (and every app composing them) reads state/actions/the
// renderer registry through.
export type { AgentChatContextValue, AgentChatSubmitOverrides } from "./parts/context";
export { useAgentChatContext } from "./parts/context";
export type { AgentChatContextWindowProps } from "./parts/context-window";
export type { AgentChatHeaderProps } from "./parts/header";
export type { AgentChatHistoryButtonProps } from "./parts/history-button";
export type { AgentChatPromptInputProps } from "./parts/prompt-input";
export type { AgentChatRootProps } from "./parts/root";
// MIN-294 — the ai app's settings context: model, usage. A descendant of
// `Root`, never an ancestor; `useAgentChatSettings` is the throwing read
// `ModelSelector`/`Context` use, so a caller reaching for it directly
// (rather than composing those parts) gets the same loud-failure guarantee.
export type { AgentChatSettingsContextValue, AgentChatSettingsProps } from "./parts/settings";
export { useAgentChatSettings, useAgentChatSettingsOptional } from "./parts/settings";
export type { AgentChatThreadProps } from "./parts/thread";

export { chatMessageQueries, chatThreadQueries } from "./queries";
export {
  evictThreadChat,
  getThreadChat,
  hydrateThreadChat,
  isThreadChatHydrated,
  resetChatRegistry,
} from "./registry";
export type { ExpandDestination } from "./resolve-expand-destination";
export { resolveExpandDestination } from "./resolve-expand-destination";
export type { SubjectBannerMode } from "./subject-banner";
export { deriveSubjectBannerMode } from "./subject-banner";
export type { ChatThread, ThreadId } from "./thread";
// MIN-292 — per-thread wiring behind the factory: apps resolve/hydrate a
// thread's shared conversation through these, never by importing
// `./thread-conversation` directly.
export type { ThreadConversationDeps } from "./thread-conversation";
export { hydrateThreadConversation, resolveThreadConversation } from "./thread-conversation";
// The one-turn model (MIN-297) — the package's public render surface. The
// engine's wire types (EngineMessage, EngineToolCall, EngineRagResult,
// EngineThread, ChatMessage, toChatMessage, toChatThread,
// sourcesFromToolCalls) stay internal; reach them via the package's deep
// imports (`@repo/agent-chat/message`, `@repo/agent-chat/thread`,
// `@repo/agent-chat/citations`) if a caller genuinely needs the wire shape.
export type { StreamTurnInput, StreamTurnMessage, TokenUsage, ToolPart, Turn, TurnOutcome, TurnPart } from "./turn";
export { turnFromHistory, turnFromStream } from "./turn";
export type { ThreadChatHandle, UseThreadChatDeps } from "./use-thread-chat";
export { useThreadChat } from "./use-thread-chat";

// MIN-293 — the connected-parts compound: `Root` / `Thread` / `PromptInput` /
// `Submit` / `Turn` join the headless `Provider` this namespace already
// carried (MIN-298). `Header`/`NewChatButton`/`HistoryButton`/`DeleteButton`
// move with them but keep their names — the issue's pinned prop surface only
// renames the five above. `ExpandButton` and `SubjectBanner` stay app-side
// (MIN-263 decision #5) — apps compose those alongside this namespace.
export const AgentChat = {
  ...AgentChatProviderNamespace,
  Root: AgentChatRoot,
  Thread: AgentChatThread,
  PromptInput: AgentChatPromptInput,
  Submit: AgentChatSubmit,
  Turn: AgentChatTurn,
  Header: AgentChatHeader,
  NewChatButton: AgentChatNewChatButton,
  HistoryButton: AgentChatHistoryButton,
  DeleteButton: AgentChatDeleteButton,
  // MIN-294 — the ai app's own settings context + the two parts that read
  // it. The provider app imports none of these.
  Settings: AgentChatSettings,
  ModelSelector: AgentChatModelSelector,
  Context: AgentChatContextWindow,
};
